import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getSuppliers,
  getSupplierById,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "services/supplier.service";
import { handleAPIError, queryClient } from "utils";
import { toast } from "react-toastify";

// Fetch all suppliers
export const useGetSuppliers = () => {
  return useQuery({
    queryKey: [queryKeys.suppliers],
    queryFn: async () => await getSuppliers(),
  });
};

// Fetch a single supplier by ID
export const useGetSupplierById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.suppliers, id], // Unique key for fetching a single supplier
    queryFn: async () => await getSupplierById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new supplier
export const useCreateSupplier = () => {
  return useMutation({
    mutationFn: createSupplier,
    onSuccess: async () => {
      // Invalidate suppliers query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.suppliers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdateSupplierPayload type
export type UpdateSupplierPayload = {
  id: string; // Required supplier ID
  data: {
    name: string; // Required name
    phone: string; // Required phone
    email: string; // Required email
    address: string; // Required address
    notes: string; // Required notes
  };
};

// Hook to update a supplier
export const useUpdateSupplier = (): UseMutationResult<
  any,
  Error,
  UpdateSupplierPayload
> => {
  return useMutation<any, Error, UpdateSupplierPayload>({
    mutationFn: async ({ id, data }) => {
      // Make sure that updateSupplier is defined to accept id and an object with supplier details
      return updateSupplier(id, data);
    },
    onSuccess: async () => {
      // Invalidate suppliers query on success
      toast.success("Supplier updated successfully.");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.suppliers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a supplier
export const useDeleteSupplier = () => {
  return useMutation({
    mutationFn: deleteSupplier,
    onSuccess: async () => {
      toast.success("Supplier deleted successfully.");

      // Invalidate suppliers query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.suppliers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
